import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flipped, Flipper, spring } from 'react-flip-toolkit';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { connect } from 'unistore/full/react';
import actions from './actions';
import './SeedPicker.scss';
import getTypeFromUri from './get-type-from-uri';
import TrackRow from './TrackRow';
import ScrollBooster from 'scrollbooster';
import getHubItemUri from './get-hub-item-uri';
import HubView from './HubView';
import store from './store';
import spfetch from './spfetch';
import produce from 'immer';
import getImagesUrlBySize from './get-images-url-by-size';
import { useScroll } from 'react-use-gesture';
import { useSpring, a, config } from 'react-spring';
import getColorForGenreUri from './get-color-for-genre-uri';
import { useFirstMountState } from './hooks';

import { Scroller, resetSavedScrollPositions } from './Scroller';

// songsBeforeEnteringAddingSongsRef.current = selected;

const onAppear = (el, index) =>
  spring({
    onUpdate: (val) => {
      el.style.opacity = val;
    },
  });

const onExit = (el, index, removeElement) => {
  spring({
    config: { overshootClamping: true },
    onUpdate: (val) => {
      el.style.opacity = 1 - val;
    },
    onComplete: removeElement,
  });

  return () => {
    el.style.opacity = '';
    removeElement();
  };
};

export default connect(
  (
    {
      contextUri,
      playerState: { paused } = {},
      preview: { uri: currentPreviewUri, isPlaying } = {},
      stationSeedsByStationId,
      stationSearchItemsByTypeAndSearchString,

      artistsByUri,
      playlistsByUri,
      genresByUri,
      moodsByUri,
      decadesByUri,
      tracksByUri,
      stationsById,
      playlistsById,
    },
    { match }
  ) => {
    const stationId = match?.params?.id;

    return {
      stationId,
      isPlaying: !paused && !!contextUri,
      currentPreviewUri,
      isPreviewPlaying: isPlaying,
      stationSearchItemsByTypeAndSearchString,

      artistsByUri,
      playlistsByUri,
      genresByUri,
      moodsByUri,
      decadesByUri,
      tracksByUri,

      sectionsByTypes: stationSeedsByStationId[stationId],
      ...stationsById[stationId],
      ...playlistsById[stationId],
    };
  },
  actions
)(React.memo(SeedPicker));

function SeedPicker(props) {
  const {
    stationId,
    sectionsByTypes,
    stationSearchItemsByTypeAndSearchString,
    seeds,
  } = props;
  const history = useHistory();

  const playlistType = useRouteMatch()?.params?.type || 'playlist';

  const [isCreateOrEditLoading, setIsCreateOrEditLoading] = useState(false);
  const [isNamingPlaylist, setIsNamingPlaylist] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [
    isDiscardConfirmationVisible,
    setIsDiscardConfirmationVisible,
  ] = useState(false);

  const [isAddingSongs, setIsAddingSongs] = useState(
    playlistType === 'playlist'
  );

  useEffect(() => {
    if (seeds == null && stationId) props.fetchDynamicPlaylist(stationId);
    props.fetchStationSeeds(stationId); // FIXME: rename
  }, [stationId]);

  const [activeTrackType, setActiveTrackType] = useState('suggested');

  const [activeType, setActiveType] = useState(
    useRouteMatch()?.params?.section || 'artist'
  );
  console.log({
    activeType,
    'useRouteMatch()?.params': useRouteMatch()?.params,
  });

  const [searchString, setSearchString] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const isSearching = isSearchFocused || !!searchString;
  const [deferredSearchString, setDeferredSearchString] = useState('');

  const itemsByType = props;

  const initialSeedUris =
    seeds?.map((seed) => seed?.uri)?.filter(Boolean) || [];
  const initialSeedUrisRef = useRef(null);
  const [selected, setSelected] = useState(new Set(initialSeedUris));

  useEffect(() => {
    if (initialSeedUris?.length && !initialSeedUrisRef.current?.length) {
      initialSeedUrisRef.current = initialSeedUris;
      setSelected(new Set(initialSeedUris));
    }
  }, [initialSeedUris]);

  const countByType = Array.from(selected)
    .map(getTypeFromUri)
    .reduce((countByType, type) => {
      countByType[type] = (countByType[type] || 0) + 1;
      return countByType;
    }, {});

  const activeSection = isAddingSongs ? activeTrackType : activeType;

  const onAdd = useCallback(
    (uri) => {
      if (!uri) return;

      const newSelected = new Set(selected);
      if (newSelected.has(uri)) newSelected.delete(uri);
      else newSelected.add(uri);
      setSelected(newSelected);

      props.toggleStationSeedsItemUri(
        stationId,
        uri,
        newSelected.has(uri),
        isSearching,
        deferredSearchString,
        activeSection
      );

      setIsSearchFocused(false);
      setSearchString('');
    },
    [selected, searchString, deferredSearchString, activeSection]
  );

  const searchType = isAddingSongs ? 'track' : activeType;
  const onSearch = useCallback(
    (event) => {
      const newSearchString = event?.target?.value;
      setSearchString(newSearchString);
      if (newSearchString)
        props.searchStationItems(activeType, newSearchString);
    },
    [activeType]
  );

  useEffect(() => {
    if (
      !searchString ||
      searchString in
        (stationSearchItemsByTypeAndSearchString[activeType] || {})
    )
      setDeferredSearchString(searchString);
    else {
      let timeout = setTimeout(() => {
        setDeferredSearchString(searchString);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [searchString]);

  const Results =
    activeType === 'artist' && !searchString && !isAddingSongs
      ? Artists
      : activeType === 'track' || isAddingSongs
      ? SectionedTrackList
      : ItemList;

  const activeSectionItems =
    (!!searchString
      ? stationSearchItemsByTypeAndSearchString[searchType]?.[
          deferredSearchString
        ]
      : itemsByType[isAddingSongs ? activeTrackType : `${activeType}s`]) || [];

  let activeTypeSections =
    (!!searchString
      ? [
          {
            items:
              sectionsByTypes?.[searchType]?.all?.filter((item) =>
                item?.name
                  ?.toLowerCase()
                  ?.includes(deferredSearchString?.toLowerCase())
              ) ||
              stationSearchItemsByTypeAndSearchString[searchType]?.[
                deferredSearchString
              ] ||
              [],
          },
        ]
      : sectionsByTypes?.[isAddingSongs ? activeTrackType : activeType]
          ?.sections) || [];

  if (isAddingSongs && activeTrackType === 'suggested') {
    const suggestedTrackUrisSet = new Set(
      activeTypeSections[0]?.items?.map((item) => item.uri)
    );

    const selectedTracksNotInSuggested = Array.from(selected)
      .filter((uri) => getTypeFromUri(uri) === 'track')
      .filter((uri) => !suggestedTrackUrisSet.has(uri));

    if (selectedTracksNotInSuggested.length) {
      activeTypeSections = [
        {
          name: '',
          items: selectedTracksNotInSuggested.map(
            (uri) => props.tracksByUri[uri]
          ),
        },
      ].concat(activeTypeSections);
    }
  }

  const selectedItems = Array.from(selected)
    .filter((uri) => getTypeFromUri(uri) === activeType)
    .map((uri) => props[`${activeType}sByUri`]?.[uri]);

  const flipKey = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    ? `FLIP support in SVG is horribly broken in Safari`
    : activeType +
      activeTypeSections
        .map((section) => section?.items?.map((item) => item?.uri))
        .flat()
        .filter(Boolean)
        .join(',') +
      Array.from(selected).join(',') +
      isSearching.toString() +
      searchString;

  useEffect(() => {
    const nameCandidateItem = Array.from(selected)
      .map((uri) => props[`${getTypeFromUri(uri)}sByUri`]?.[uri])
      ?.filter(Boolean)?.[0];

    let nameCandidate = nameCandidateItem?.name;
    if (getTypeFromUri(nameCandidateItem?.uri) === 'genre') {
      nameCandidate = nameCandidate?.replace(/((?:^|\b).)/g, (firstLetter) =>
        firstLetter.toUpperCase()
      );
    }

    if (!playlistName && selected?.size) {
      setPlaylistName(
        nameCandidate
          ? selected.size > 1
            ? `${nameCandidate} and more`
            : nameCandidate
          : 'My Playlist'
      );
    }
  }, [isNamingPlaylist]);

  const songsBeforeEnteringAddingSongsRef = useRef(null);

  const hasPendingChanges =
    initialSeedUrisRef.current != null &&
    initialSeedUrisRef.current?.concat()?.sort().join(',') !==
      Array.from(selected).sort().join(',');

  const onClose = () => {
    const initialUris = initialSeedUrisRef.current?.concat()?.sort();
    const currentUris = Array.from(selected).sort();

    if (initialUris?.join(',') === currentUris?.join(',')) {
      history.goBack();
    } else {
      setIsDiscardConfirmationVisible(true);
    }
  };

  const onCloseDetails = () => {
    if (
      Array.from(selectedUrisBeforeEnteringDetailsRef.current)
        ?.concat()
        ?.sort()
        ?.join(',') === Array.from(selected).sort()?.join(',')
    ) {
      setActiveType(null);
      setIsAddingSongs(false);
    } else {
      setIsDiscardConfirmationVisible(true);
    }
  };

  useEffect(() => {
    Object.entries(
      Array.from(selected)
        .filter((uri) => {
          const type = getTypeFromUri(uri);
          return (
            (type === 'track' || type === 'artist') &&
            !props[`${type}sByUri`]?.[uri]
          );
        })
        .reduce(
          (urisByType, uri) => {
            urisByType[getTypeFromUri(uri)].push(uri);
            return urisByType;
          },
          { track: [], artist: [] }
        )
    )
      .filter(([, uris]) => uris?.length)
      .forEach(([type, uris]) =>
        spfetch(
          `https://api.spotify.com/v1/${type}s?ids=${uris
            .map((uri) => uri.substr(-22))
            .join(',')}`
        ).then(({ [`${type}s`]: items }) =>
          store.setState(
            produce(store.getState(), (draft) => {
              items.forEach((item) => {
                item.image = getImagesUrlBySize(
                  item?.images || item?.album?.images,
                  112
                );

                draft[`${type}sByUri`][item?.uri] = item;
              });
            })
          )
        )
      );
  }, [selected]);

  const selectedUrisBeforeEnteringDetailsRef = useRef(null);
  useEffect(() => {
    selectedUrisBeforeEnteringDetailsRef.current = selected;
  }, [activeType]);

  const scrollId =
    activeType?.toString() +
    (activeType === 'track' ? activeTrackType?.toString() : '') +
    isSearching?.toString();

  const scrollViewportRef = useRef(null);

  if (isCreateOrEditLoading)
    return (
      <div className="SeedPicker__loading">
        <div className="SeedPicker__loading-text">Loading…</div>
        <div className="SeedPicker__loading-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="12px"
            width="56px"
            x="0px"
            y="0px"
            viewBox="0 0 1 100"
            xmlSpace="preserve"
            data-testid="loadingIcon"
          >
            <circle
              className="SeedPicker__loading-icon-circle"
              cx="-140"
              cy="50"
              r="32"
            />
            <circle
              className="SeedPicker__loading-icon-circle"
              cx="0"
              cy="50"
              r="32"
            />
            <circle
              className="SeedPicker__loading-icon-circle"
              cx="140"
              cy="50"
              r="32"
            />
          </svg>
        </div>
      </div>
    );

  if (isNamingPlaylist) {
    return (
      <div className="SeedPicker" style={{ background: '#121212' }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 375 275"
        >
          <path
            fill="#121212"
            d="M0 0H375V127H0z"
            transform="translate(0 148)"
          ></path>
          <path
            stroke="#535353"
            strokeLinecap="square"
            d="M16 197.747l343-.494"
          ></path>
          <path fill="#121212" d="M327 156H359V197H327z" opacity="0.8"></path>
          <path
            fill="#fff"
            d="M349.354 169.353l-.708-.707-5.646 5.647-5.647-5.647-.707.707 5.647 5.647-5.647 5.646.707.708 5.647-5.647 5.646 5.647.708-.708-5.647-5.646 5.647-5.647z"
            opacity="0.7"
          ></path>
          <text
            fill="#fff"
            fillOpacity="0.7"
            style={{ whiteSpace: 'pre' }}
            fontSize="13"
            fontWeight="450"
            letterSpacing="0"
          >
            <tspan x="16" y="229.257">
              Here’s a name based on your picks. Want to call your{' '}
            </tspan>
            <tspan x="16" y="247.257">
              playlist something else? Go for it!{' '}
            </tspan>
          </text>

          <g
            id="name-playlist-header"
            onClick={() => setIsNamingPlaylist(false)}
          >
            <path
              fill="#121212"
              d="M0 8a8 8 0 018-8h359a8 8 0 018 8v96H0V8z"
            ></path>
          </g>

          <foreignObject width="311" height="50" x="16" y="148">
            <input
              type="text"
              value={playlistName}
              placeholder="Playlist name"
              className="SeedPicker__input-name"
              onChange={(event) => setPlaylistName(event?.target?.value)}
            />
          </foreignObject>

          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="16"
            fontWeight="bold"
            letterSpacing="0em"
          >
            <tspan x="136.812" y="33.24">
              Name playlist
            </tspan>
          </text>
          <path
            fill="#fff"
            d="M31.54 37.151l-10.445-8.922 10.445-8.92.65.76-9.555 8.16 9.555 8.161-.65.761z"
          ></path>
        </svg>

        <svg
          className="SeedPicker__create-button-wrapper"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 375 192"
          style={{ pointerEvents: 'none' }}
        >
          <path fill="#121212" d="M0 96H375V192H0z"></path>
          <path
            fill="url(#create-button-wrapper-gradient)"
            d="M0 48H375V96H0z"
          ></path>

          <defs>
            <linearGradient
              id="create-button-wrapper-gradient"
              x1="187.5"
              x2="187.5"
              y1="48"
              y2="96"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#121212" stopOpacity="0"></stop>
              <stop offset="1" stopColor="#121212"></stop>
            </linearGradient>
          </defs>
        </svg>

        <svg
          className="SeedPicker__create-button-wrapper"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 375 96"
        >
          <g
            transform="translate(0, -96)"
            style={{
              pointerEvents: 'bounding-box',
            }}
            onClick={() => {
              setIsCreateOrEditLoading(true);
              props.editOrCreateDynamicPlaylist(
                undefined,
                playlistName,
                selected,
                history
              );
              resetSavedScrollPositions('seed-picker');
            }}
          >
            <g transform="translate(119, 102)">
              <path
                fill="#fff"
                d="M0 24C0 10.745 10.745 0 24 0h89c13.255 0 24 10.745 24 24s-10.745 24-24 24H24C10.745 48 0 37.255 0 24z"
              ></path>
              <text
                fill="#121212"
                style={{ whiteSpace: 'pre' }}
                fontSize="14"
                fontWeight="bold"
                letterSpacing=".1em"
              >
                <tspan x="38" y="28.585">
                  CREATE
                </tspan>
              </text>
            </g>
          </g>
        </svg>
      </div>
    );
  }

  return (
    <>
      {isDiscardConfirmationVisible && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 375 812"
          className="SeedPicker__discard-confirmation"
        >
          <path fill="#000" fillOpacity="0.6" d="M0 0H375V812H0z"></path>
          <g filter="url(#discard-confirmation-filter0_d)">
            <path
              fill="#F2F2F2"
              d="M53 350c0-7.732 6.268-14 14-14h242c7.732 0 14 6.268 14 14v112c0 7.732-6.268 14-14 14H67c-7.732 0-14-6.268-14-14V350z"
            ></path>
            <text
              fill="#000"
              style={{ whiteSpace: 'pre' }}
              fontFamily="SF Pro Text"
              fontSize="17"
              fontWeight="600"
              letterSpacing="-0.408"
            >
              <tspan x="116.901" y="373.043">
                Discard changes?
              </tspan>
            </text>
            <text
              fill="#000"
              style={{ whiteSpace: 'pre' }}
              fontFamily="SF Pro Text"
              fontSize="13"
              letterSpacing="-0.078"
            >
              <tspan x="88.63" y="392.621">
                Changes you’ve made will not be{' '}
              </tspan>
              <tspan x="168.301" y="408.621">
                saved.
              </tspan>
            </text>

            <g onClick={() => history.goBack()}>
              <rect
                width="134"
                height="44"
                fill="#fff"
                fillOpacity="0.001"
                x="188"
                y="432"
              />
              <text
                fill="#007AFF"
                style={{ whiteSpace: 'pre' }}
                fontFamily="SF Pro Text"
                fontSize="17"
                fontWeight="600"
                letterSpacing="-0.408"
              >
                <tspan x="241.124" y="460.043">
                  Yes
                </tspan>
              </text>
            </g>

            <g
              onClick={() =>
                setIsDiscardConfirmationVisible(!isDiscardConfirmationVisible)
              }
            >
              <rect
                width="134"
                height="44"
                fill="#fff"
                fillOpacity="0.001"
                x="53"
                y="432"
              />
              <text
                fill="#007AFF"
                style={{ whiteSpace: 'pre' }}
                fontFamily="SF Pro Text"
                fontSize="17"
                letterSpacing="-0.408"
              >
                <tspan x="94.028" y="460.043">
                  Cancel
                </tspan>
              </text>
            </g>
            <path
              fill="#3C3C43"
              fillOpacity="0.29"
              d="M187.5 432.5H188V476H187.5z"
            ></path>
            <path
              fill="#3C3C43"
              fillOpacity="0.29"
              d="M53 432H323V432.5H53z"
            ></path>
          </g>
          <defs>
            <filter
              id="discard-confirmation-filter0_d"
              width="370"
              height="240"
              x="3"
              y="290"
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
              <feColorMatrix
                in="SourceAlpha"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              ></feColorMatrix>
              <feOffset dy="4"></feOffset>
              <feGaussianBlur stdDeviation="25"></feGaussianBlur>
              <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"></feColorMatrix>
              <feBlend
                in2="BackgroundImageFix"
                result="effect1_dropShadow"
              ></feBlend>
              <feBlend
                in="SourceGraphic"
                in2="effect1_dropShadow"
                result="shape"
              ></feBlend>
            </filter>
          </defs>
        </svg>
      )}

      <Flipper flipKey={flipKey} spring="noWobble">
        <div
          className={`SeedPicker ${
            isSearching ? 'SeedPicker--is-searching' : ''
          }`}
        >
          <div
            className={`SeedPicker__header ${
              isSearching ? 'SeedPicker__header--is-searching' : ''
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 375 56"
            >
              <g clipPath="url(#create-or-edit-station-header)">
                <path
                  fill="#121212"
                  d="M0 16C0 7.163 7.163 0 16 0h343c8.837 0 16 7.163 16 16v40H0V16z"
                ></path>

                <g transform="translate(0, 0)" onClick={onClose}>
                  <rect width="56" height="56" fill="#121212" x="0" rx="8" />
                  <path
                    d="M36.354 20.353L35.646 19.647L28 27.293L20.353 19.647L19.647 20.353L27.293 28L19.647 35.646L20.353 36.354L28 28.707L35.646 36.354L36.354 35.646L28.707 28L36.354 20.353Z"
                    fill="white"
                  />
                </g>

                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="16"
                  fontWeight="bold"
                  letterSpacing="0em"
                >
                  <tspan x="189.721" y="33.24" textAnchor="middle">
                    {playlistType === 'playlist'
                      ? 'Add songs'
                      : 'Choose any combination'}
                  </tspan>
                </text>

                {/* <g
                onClick={history.goBack}
                style={{
                  pointerEvents: 'bounding-box',
                }}
              >
                <rect width="56" height="56" fill="#121212" x="0" rx="8" />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M36.354 26.353l-.708-.706L28 33.293l-7.647-7.646-.706.706L27.293 34l-7.646 7.646.706.708L28 34.707l7.646 7.647.708-.708L28.707 34l7.647-7.647z"
                  clipRule="evenodd"
                ></path>
              </g> */}
              </g>
              <defs>
                <clipPath id="create-or-edit-station-header">
                  <path fill="#fff" d="M0 0H375V56H0z"></path>
                </clipPath>
              </defs>
            </svg>

            {isAddingSongs && !isSearching ? (
              <div className="SeedPicker__toolbar-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="523"
                  height="44"
                  fill="none"
                  viewBox="0 0 523 44"
                >
                  <path fill="#121212" d="M0 0H523V44H0z"></path>

                  <g
                    transform="translate(-16, 0)"
                    style={{ pointerEvents: 'bounding-box' }}
                    onClick={(event) => {
                      setActiveTrackType('suggested');
                    }}
                  >
                    <rect width="111" height="44" fill="#121212" x="16" />
                    <text
                      fill="#fff"
                      style={{
                        whiteSpace: 'pre',
                        willChange: 'opacity',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: activeTrackType === 'suggested' ? 1 : 0.42,
                      }}
                      fontSize="16"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="32" y="27.24">
                        Suggested
                      </tspan>
                    </text>
                  </g>

                  <g
                    transform="translate(-114, 0)"
                    style={{ pointerEvents: 'bounding-box' }}
                    onClick={(event) => {
                      setActiveTrackType('recently_played');
                    }}
                  >
                    <rect width="150" height="44" fill="#121212" x="216" />
                    <text
                      fill="#fff"
                      style={{
                        whiteSpace: 'pre',
                        willChange: 'opacity',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity:
                          activeTrackType === 'recently_played' ? 1 : 0.42,
                      }}
                      fontSize="16"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="232" y="27.24">
                        Recently Played
                      </tspan>
                    </text>
                  </g>

                  <g
                    transform="translate(-170, 0)"
                    style={{ pointerEvents: 'bounding-box' }}
                    onClick={(event) => {
                      setActiveTrackType('liked_songs');
                    }}
                  >
                    <rect width="148" height="44" fill="#121212" x="410" />
                    <text
                      fill="#fff"
                      style={{
                        whiteSpace: 'pre',
                        willChange: 'opacity',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: activeTrackType === 'liked_songs' ? 1 : 0.42,
                      }}
                      fontSize="16"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="426" y="27.24">
                        Liked songs
                      </tspan>
                    </text>
                  </g>

                  <g
                    transform="translate(0, 42)"
                    key={isAddingSongs?.toString()}
                  >
                    <g
                      style={{
                        transformOrigin: 'center left',
                        transform: {
                          suggested: `translate3d(16px, 0, 0) scaleX(0.79)`,
                          recently_played: `translate3d(118px, 0, 0) scaleX(1.19)`,
                          liked_songs: `translate3d(256px, 0, 0) scaleX(0.88)`,
                        }[activeTrackType],
                        willChange: 'transform',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <rect width="100" height="2" fill="#1DB954" />
                    </g>
                  </g>
                </svg>
              </div>
            ) : null}

            {!isAddingSongs && !isSearching && (
              <div className="SeedPicker__toolbar-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="523"
                  height="44"
                  fill="none"
                  viewBox="0 0 523 44"
                >
                  <path fill="#121212" d="M0 0H523V44H0z"></path>

                  <g
                    transform="translate(-16, 0)"
                    style={{ pointerEvents: 'bounding-box' }}
                    onClick={(event) => {
                      setActiveType('artist');
                    }}
                  >
                    <rect width="81" height="44" fill="#121212" x="16" />
                    <text
                      fill="#fff"
                      style={{
                        whiteSpace: 'pre',
                        willChange: 'opacity',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: activeType === 'artist' ? 1 : 0.42,
                      }}
                      fontSize="16"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="32" y="27.24">
                        Artists
                      </tspan>
                    </text>

                    {countByType?.artist && (
                      <g>
                        <path
                          fill="#fff"
                          fillRule="evenodd"
                          d="M90 20a8 8 0 100-16 8 8 0 000 16z"
                          clipRule="evenodd"
                        ></path>
                        <text
                          fill="#181818"
                          style={{ whiteSpace: 'pre' }}
                          fontSize="9"
                          fontWeight="bold"
                          letterSpacing="0"
                        >
                          <tspan x="90" y="14.947" textAnchor="middle">
                            {countByType?.artist}
                          </tspan>
                        </text>
                      </g>
                    )}
                  </g>

                  <g
                    transform="translate(-134, 0)"
                    style={{ pointerEvents: 'bounding-box' }}
                    onClick={(event) => {
                      setActiveType('genre');
                    }}
                  >
                    <rect width="85" height="44" fill="#121212" x="216" />
                    <text
                      fill="#fff"
                      style={{
                        whiteSpace: 'pre',
                        willChange: 'opacity',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: activeType === 'genre' ? 1 : 0.42,
                      }}
                      fontSize="16"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="232" y="27.24">
                        Genres
                      </tspan>
                    </text>

                    {countByType?.genre && (
                      <g transform="translate(204, 0)">
                        <path
                          fill="#fff"
                          fillRule="evenodd"
                          d="M90 20a8 8 0 100-16 8 8 0 000 16z"
                          clipRule="evenodd"
                        ></path>
                        <text
                          fill="#181818"
                          style={{ whiteSpace: 'pre' }}
                          fontSize="9"
                          fontWeight="bold"
                          letterSpacing="0"
                        >
                          <tspan x="90" y="14.947" textAnchor="middle">
                            {countByType?.genre}
                          </tspan>
                        </text>
                      </g>
                    )}
                  </g>

                  <g
                    transform="translate(-242, 0)"
                    style={{ pointerEvents: 'bounding-box' }}
                    onClick={(event) => {
                      setActiveType('decade');
                    }}
                  >
                    <rect width="96" height="44" fill="#121212" x="410" />
                    <text
                      fill="#fff"
                      style={{
                        whiteSpace: 'pre',
                        willChange: 'opacity',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: activeType === 'decade' ? 1 : 0.42,
                      }}
                      fontSize="16"
                      fontWeight="bold"
                      letterSpacing="0em"
                    >
                      <tspan x="426" y="27.24">
                        Decades
                      </tspan>
                    </text>

                    {countByType?.decade && (
                      <g transform="translate(409, 0)">
                        <path
                          fill="#fff"
                          fillRule="evenodd"
                          d="M90 20a8 8 0 100-16 8 8 0 000 16z"
                          clipRule="evenodd"
                        ></path>
                        <text
                          fill="#181818"
                          style={{ whiteSpace: 'pre' }}
                          fontSize="9"
                          fontWeight="bold"
                          letterSpacing="0"
                        >
                          <tspan x="90" y="14.947" textAnchor="middle">
                            {countByType?.decade}
                          </tspan>
                        </text>
                      </g>
                    )}
                  </g>

                  <g
                    transform="translate(0, 42)"
                    key={isAddingSongs?.toString()}
                  >
                    <g
                      style={{
                        transformOrigin: 'center left',
                        transform: {
                          artist: `translate3d(16px, 0, 0) scaleX(0.49)`,
                          genre: `translate3d(98px, 0, 0) scaleX(0.53)`,
                          decade: `translate3d(184px, 0, 0) scaleX(0.64)`,
                        }[activeType],
                        willChange: 'transform',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <rect width="100" height="2" fill="#1DB954" />
                    </g>
                  </g>
                </svg>
              </div>
            )}
          </div>

          <Scroller
            sectionId="seed-picker"
            scrollId={scrollId}
            className={`SeedPicker__content ${
              isSearching ? 'SeedPicker__content--is-searching' : ''
            }`}
            onScroller={
              (scroller) => {
                scrollViewportRef.current = scroller?.props?.viewport;
              }
              // (scrollViewportRef.current = scroller?.viewport)
            }
          >
            <div
              style={{
                paddingBottom: '92px',
              }}
            >
              <Flipped flipId="search">
                <div className="SeedPicker__header-search-wrapper">
                  <div className="SeedPicker__header-search-field-wrapper">
                    <input
                      type="text"
                      className="SeedPicker__header-search-field-input"
                      placeholder="Search"
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      onChange={onSearch}
                      value={searchString}
                    />

                    <div
                      className={`SeedPicker__header-search-field-cancel ${
                        isSearching
                          ? 'SeedPicker__header-search-field-cancel--is-searching'
                          : ''
                      }`}
                      onPointerDown={() => {
                        setSearchString('');
                        setIsSearchFocused(false);
                        if (document.activeElement?.blur)
                          document.activeElement.blur();
                      }}
                    >
                      Cancel
                    </div>

                    {searchString && (
                      <div
                        className={`SeedPicker__header-search-field-cross-wrapper ${
                          isSearching
                            ? 'SeedPicker__header-search-field-cross-wrapper--is-searching'
                            : ''
                        }`}
                        onPointerDown={() => {
                          setSearchString('');
                          setIsSearchFocused(false);
                          if (document.activeElement?.blur)
                            document.activeElement.blur();
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 16 16"
                          className="SeedPicker__header-search-field-cross"
                        >
                          <path
                            fill="#fff"
                            d="M14.354 2.353l-.708-.707L8 7.293 2.353 1.646l-.707.707L7.293 8l-5.647 5.646.707.708L8 8.707l5.646 5.647.708-.708L8.707 8l5.647-5.647z"
                          ></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </Flipped>

              <Results
                type={activeType}
                sections={activeTypeSections}
                items={activeSectionItems}
                selectedItems={selectedItems}
                selected={selected}
                onAdd={onAdd}
                isSearching={!!searchString}
              />
            </div>
          </Scroller>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 375 106"
            className="SeedPicker__create-button-wrapper"
            style={{
              willChange: 'transform',
              transform:
                !isSearching && (selected.size || playlistType === 'playlist')
                  ? 'translate3d(0, 0, 0)'
                  : 'translate3d(0, 100%, 0)',
              transition: 'transform 0.2s ease-in-out',
            }}
          >
            <path
              fill="url(#create-button-wrapper-gradient)"
              d="M0 0H375V106H0z"
            ></path>

            <g
              onClick={() => {
                if (!selected.size && playlistType === 'dynamic-playlist')
                  return;

                if (stationId) {
                  if (hasPendingChanges) {
                    if (selectedUrisBeforeEnteringDetailsRef)
                      setIsCreateOrEditLoading(true);
                    props.editOrCreateDynamicPlaylist(
                      stationId,
                      playlistName,
                      selected,
                      history
                    );
                  } else {
                    history.goBack();
                  }
                  resetSavedScrollPositions('seed-picker');
                } else {
                  setIsNamingPlaylist(true);
                }
              }}
            >
              <path
                fill="#fff"
                d="M117 40c0-13.255 10.745-24 24-24h93c13.255 0 24 10.745 24 24s-10.745 24-24 24h-93c-13.255 0-24-10.745-24-24z"
              ></path>

              <text
                fill="#121212"
                style={{ whiteSpace: 'pre' }}
                fontSize="14"
                fontWeight="bold"
                letterSpacing=".1em"
              >
                <tspan x="187.504" y="44.585" textAnchor="middle">
                  {stationId ? 'DONE' : 'NEXT'}
                </tspan>
              </text>
            </g>

            <defs>
              <linearGradient
                id="create-button-wrapper-gradient"
                x1="187.5"
                x2="187.5"
                y1="0"
                y2="106"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#121212" stopOpacity="0"></stop>
                <stop offset="1" stopColor="#121212"></stop>
              </linearGradient>
            </defs>
          </svg>
        </div>
      </Flipper>
    </>
  );
}

const Artists = function Artists({
  sections: artistSections,
  selectedItems: selectedArtists,
  onAdd,
  selected,
  isSearching,
}) {
  const TITLE_HEIGHT = 48;
  const ROW_HEIGHT = 176;
  let y = 0;

  const isFirstMount = useFirstMountState();
  const scrollBoosterRef = useRef(null);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox={`0 0 375 ${
        artistSections?.length * TITLE_HEIGHT +
        ROW_HEIGHT *
          (artistSections?.reduce((numRows, section) => {
            return numRows + Math.ceil(section?.items?.length / 3);
          }, 0) || 0) +
        (selectedArtists?.length ? 116 : 16)
      }`}
    >
      <foreignObject width="375" height="100" x="0" y="0">
        <Flipped flipId="selected" onAppear={onAppear} /*onExit={onExit}*/>
          {!!selectedArtists?.length && !isSearching && (
            <Scroller
              sectionId="seed-picker"
              scrollId="artists-list"
              className="SeedPicker__selected-wrapper"
              direction="horizontal"
              onScroller={(scrollBooster) =>
                (scrollBoosterRef.current = scrollBooster)
              }
            >
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  width={Math.max(375, 91 * (selectedArtists?.length || 0))}
                  height={100}
                  fill="none"
                  viewBox={`0 0 ${Math.max(
                    375,
                    91 * (selectedArtists?.length || 0)
                  )} 100`}
                >
                  {selectedArtists.map(({ uri, name, image }, i) => {
                    return (
                      <Flipped
                        key={`${uri}-selected`}
                        flipId={`${uri}-selected`}
                        onAppear={(el, index) => {
                          if (!isFirstMount) {
                            const { left } = el.getBoundingClientRect();

                            scrollBoosterRef.current.updateMetrics();

                            const posX = Math.max(
                              Math.min(
                                scrollBoosterRef.current?.position.x - left,
                                scrollBoosterRef.current.edgeX.to
                              ),
                              scrollBoosterRef.current.edgeX.from
                            );

                            scrollBoosterRef.current.scrollTo({
                              x: -posX,
                            });
                          }

                          return spring({
                            onUpdate: (val) => {
                              el.style.opacity = val;
                            },
                          });
                        }}
                      >
                        <g
                          transform={`translate(${91 * i}, 0)`}
                          style={{ pointerEvents: 'bounding-box' }}
                          onClick={() => onAdd(uri)}
                        >
                          <circle
                            fill={`url(#${image})`}
                            cx={49}
                            cy={38}
                            r="30"
                          />
                          <text
                            fill="#fff"
                            style={{ whiteSpace: 'pre' }}
                            fontSize="11"
                            fontWeight="450"
                            letterSpacing="0em"
                          >
                            <tspan x="48" y="83.603" textAnchor="middle">
                              {truncate(name, 16)}
                            </tspan>
                          </text>

                          <circle
                            cx="71"
                            cy="16"
                            r="12"
                            fill="#282828"
                          ></circle>
                          <path
                            fill="#fff"
                            d="M77.354 10.353l-.708-.707L71 15.293l-5.647-5.647-.707.707L70.293 16l-5.647 5.646.707.708L71 16.707l5.646 5.647.708-.708L71.707 16l5.647-5.647z"
                          ></path>

                          {image && (
                            <defs>
                              <pattern
                                id={image}
                                width="1"
                                height="1"
                                patternContentUnits="objectBoundingBox"
                              >
                                <image
                                  width="208"
                                  height="208"
                                  xlinkHref={image}
                                  transform="scale(.0048)"
                                  preserveAspectRatio="xMidYMin slice"
                                />
                              </pattern>
                            </defs>
                          )}
                        </g>
                      </Flipped>
                    );
                  })}
                </svg>
              </div>
            </Scroller>
          )}
        </Flipped>
      </foreignObject>

      {artistSections &&
        artistSections?.map(({ name, items }) => {
          const section = (
            <g
              key={`artist-section-${name}`}
              transform={`translate(0, ${
                isSearching ? 0 : selectedArtists?.length ? 124 + y : 24 + y
              })`}
            >
              <Flipped flipId={`artist-${name}`}>
                <g opacity={isSearching ? 0 : 1}>
                  <text
                    fill="#fff"
                    style={{ whiteSpace: 'pre' }}
                    fontSize="16"
                    fontWeight="450"
                    letterSpacing="0em"
                    opacity="0.7"
                  >
                    <tspan x="16" y="5">
                      {name}
                    </tspan>
                  </text>
                </g>
              </Flipped>

              {items.map(({ uri, name, image }, i) => {
                return (
                  <Flipped
                    key={`artist-${uri}`}
                    flipId={`artist-${uri}`}
                    onAppear={onAppear} /*onExit={onExit}*/
                  >
                    <g
                      transform={`translate(${118 * (i % 3)}, ${
                        176 * Math.floor(i / 3)
                      })`}
                      style={{ pointerEvents: 'bounding-box' }}
                      onClick={() => onAdd(uri)}
                    >
                      <g>
                        <circle
                          fill={`url(#${image || 'no-artist-image'})`}
                          cx={68}
                          cy={76}
                          r="52"
                          style={{
                            opacity: selected.has(uri) ? 1 : 0.9,
                            willChange: 'opacity',
                            transition: 'opacity 0.2s ease-in-out',
                          }}
                        />
                        <foreignObject x="17" y="138" width="104" height="40">
                          <p className="SeedPicker__artists-item-title">
                            {name}
                          </p>
                        </foreignObject>
                      </g>

                      {selected && selected.has(uri) && (
                        <g transform="translate(88, 24)">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z"
                            fill="white"
                          />
                          <path
                            d="M10 17.1652L13.8307 21.5L22.8 11"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="square"
                          />
                        </g>
                      )}

                      {image && (
                        <defs>
                          <pattern
                            id={image}
                            width="1"
                            height="1"
                            patternContentUnits="objectBoundingBox"
                          >
                            <image
                              width="208"
                              height="208"
                              xlinkHref={image}
                              transform="scale(.0048)"
                              preserveAspectRatio="xMidYMin slice"
                            />
                          </pattern>
                        </defs>
                      )}
                    </g>
                  </Flipped>
                );
              })}
            </g>
          );
          y += TITLE_HEIGHT + ROW_HEIGHT * Math.ceil(items.length / 3);
          return section;
        })}
      <defs>
        <pattern
          id="no-artist-image"
          x="0"
          y="0"
          width="104"
          height="104"
          patternUnits="userSpaceOnUse"
          preserveAspectRatio="xMidYMid slice"
        >
          <rect x="0" y="0" width="104" height="104" fill="#333" />
          <g transform="translate(44, 32)">
            <path
              d="M32.88 29.393L30.001 27.738C29.647 27.535 29.544 27.214 29.514 27.042C29.484 26.869 29.474 26.533 29.739 26.223L32.36 23.15C34.062 21.161 35 18.622 35 16.001V15.001C35 11.973 33.732 9.047 31.521 6.975C29.279 4.873 26.345 3.83 23.269 4.025C17.51 4.399 12.999 9.371 12.999 15.344V16C12.999 18.621 13.937 21.16 15.638 23.148L18.26 26.222C18.525 26.532 18.515 26.868 18.485 27.041C18.456 27.214 18.352 27.534 17.999 27.737L11.572 31.43C8.134 33.383 6 37.051 6 41V44H8V41.002C8 37.771 9.747 34.77 12.565 33.168L18.997 29.473C19.772 29.027 20.305 28.265 20.457 27.383C20.609 26.501 20.364 25.605 19.783 24.924L17.16 21.849C15.767 20.222 15.001 18.145 15.001 16V15.343C15.001 10.42 18.69 6.325 23.399 6.02C25.925 5.848 28.319 6.713 30.154 8.433C31.99 10.154 33.001 12.486 33.001 15V16C33.001 18.145 32.234 20.222 30.841 21.85L28.219 24.924C27.638 25.605 27.393 26.501 27.545 27.383C27.697 28.265 28.231 29.027 29.007 29.473L30.659 30.423C31.349 29.996 32.091 29.644 32.88 29.393ZM40 23.254V34.506C38.92 33.525 37.492 32.922 35.922 32.922C32.57 32.922 29.844 35.648 29.844 39C29.844 42.352 32.571 45.078 35.922 45.078C39.273 45.077 42 42.351 42 39V26.718L46.035 29.047L47.035 27.315L40 23.254ZM35.922 43.077C33.673 43.077 31.844 41.248 31.844 38.999C31.844 36.75 33.674 34.921 35.922 34.921C38.17 34.922 40 36.751 40 39C40 41.249 38.17 43.077 35.922 43.077Z"
              fill="white"
            />
          </g>
        </pattern>
      </defs>
    </svg>
  );
};

function ItemList({
  type,
  sections,
  selectedItems,
  selected,
  onAdd,
  isSearching,
}) {
  const TITLE_HEIGHT = 29;
  const ROW_HEIGHT = type === 'playlist' ? 80 : 64;
  const SELECTED_HEIGHT = 72;

  const height =
    (isSearching
      ? 0
      : TITLE_HEIGHT * sections?.filter(({ name }) => name).length) +
    sections?.map(({ items }) => items).flat().length * ROW_HEIGHT +
    (selectedItems?.length && !isSearching ? SELECTED_HEIGHT : 0);
  let y = 0;

  const scrollBoosterRef = useRef(null);
  const isFirstMount = useFirstMountState();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox={`0 0 375 ${height}`}
    >
      {!!selectedItems?.length && !isSearching && (
        <foreignObject width="375" height={SELECTED_HEIGHT} x="0" y="0">
          <Flipped
            flipId={`selected-${type}`}
            onAppear={onAppear} /*onExit={onExit}*/
          >
            {!!selectedItems?.length && (
              <Scroller
                sectionId="seed-picker"
                scrollId={`${type}-item-list`}
                className="SeedPicker__selected-item-list-wrapper"
                direction="horizontal"
                onScroller={(scrollBooster) =>
                  (scrollBoosterRef.current = scrollBooster)
                }
              >
                <div>
                  {selectedItems.map(({ uri, name }, i) => (
                    <Flipped
                      flipId={`${uri}-selected`}
                      onAppear={(el, index) => {
                        if (!isFirstMount) {
                          const { left } = el.getBoundingClientRect();

                          scrollBoosterRef.current.updateMetrics();

                          const posX = Math.max(
                            Math.min(
                              scrollBoosterRef.current?.position.x - left,
                              scrollBoosterRef.current.edgeX.to
                            ),
                            scrollBoosterRef.current.edgeX.from
                          );

                          scrollBoosterRef.current.scrollTo({
                            x: -posX,
                          });
                        }

                        return spring({
                          onUpdate: (val) => {
                            el.style.opacity = val;
                          },
                        });
                      }}
                    >
                      <div className="SeedPicker__selected-item-list-pill-wrapper">
                        <div
                          className="SeedPicker__selected-item-list-pill"
                          onClick={() => onAdd(uri)}
                        >
                          <span>{name}</span>
                        </div>
                      </div>
                    </Flipped>
                  ))}
                </div>
              </Scroller>
            )}
          </Flipped>
        </foreignObject>
      )}

      {sections?.map(({ name, items }) => {
        const section = (
          <g
            transform={`translate(0, ${
              isSearching
                ? 0
                : selectedItems?.length && !isSearching
                ? SELECTED_HEIGHT + (name ? TITLE_HEIGHT : 0) + y
                : (name ? TITLE_HEIGHT : 0) + y
            })`}
          >
            <Flipped flipId={`${type}-${name}-title`}>
              <g opacity={isSearching ? 0 : 1}>
                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="16"
                  fontWeight="450"
                  letterSpacing="0em"
                  opacity="0.7"
                >
                  <tspan x="16" y="0">
                    {name}
                  </tspan>
                </text>
              </g>
            </Flipped>

            {items.map((item, i) => {
              return (
                <Flipped
                  flipId={item?.uri}
                  onAppear={onAppear} /*onExit={onExit}*/
                >
                  <g
                    style={{ pointerEvents: 'bounding-box' }}
                    transform={`translate(0, ${ROW_HEIGHT * i})`}
                    onClick={() => onAdd(item?.uri)}
                  >
                    <rect
                      width="375"
                      height={ROW_HEIGHT}
                      fill="#000"
                      x="0"
                      fillOpacity="0.001"
                    />

                    <g
                      transform={`translate(343, ${
                        type === 'playlist' ? 32 : 24
                      })`}
                    >
                      {selected.has(item?.uri) ? (
                        <path
                          d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8.33 15.62L4.32 10.93L5.3 9.77L8.33 13.31L14.66 5.9L15.8 6.87L8.33 15.62Z"
                          fill="white"
                        />
                      ) : (
                        <path
                          d="M10.0008 4.16667H9.1675V9.16667H4.1675V10H9.1675V15H10.0008V10H15.0008V9.16667H10.0008V4.16667ZM9.58333 0C4.29083 0 0 4.29083 0 9.58333C0 14.8758 4.29083 19.1667 9.58333 19.1667C14.8758 19.1667 19.1667 14.8758 19.1667 9.58333C19.1667 4.29083 14.8758 0 9.58333 0ZM9.58333 18.3333C4.75833 18.3333 0.833333 14.4083 0.833333 9.58333C0.833333 4.75833 4.75833 0.833333 9.58333 0.833333C14.4083 0.833333 18.3333 4.75833 18.3333 9.58333C18.3333 14.4083 14.4083 18.3333 9.58333 18.3333Z"
                          fill="#B3B3B3"
                        />
                      )}
                    </g>

                    {type === 'artist' ? (
                      <>
                        <circle
                          cx="40"
                          cy="32"
                          r="24"
                          fill={`url(#${item?.image})`}
                        />

                        <text
                          fill="#fff"
                          style={{ whiteSpace: 'pre' }}
                          fontSize="16"
                          letterSpacing="0em"
                        >
                          <tspan x="76" y="37.24">
                            {truncate(item?.name, 24)}
                          </tspan>
                        </text>

                        {item?.image && (
                          <defs>
                            <pattern
                              id={item?.image}
                              width="1"
                              height="1"
                              patternContentUnits="objectBoundingBox"
                            >
                              <use
                                transform="scale(.00333)"
                                xlinkHref={`#${item?.image}-image`}
                              ></use>
                            </pattern>
                            <image
                              id={`${item?.image}-image`}
                              width="300"
                              height="300"
                              xlinkHref={item?.image}
                              preserveAspectRatio="xMinYMin slice"
                            ></image>
                          </defs>
                        )}
                      </>
                    ) : type === 'playlist' ? (
                      <>
                        <text
                          fill="#B3B3B3"
                          style={{ whiteSpace: 'pre' }}
                          fontSize="13"
                          fontWeight="450"
                          letterSpacing="0em"
                        >
                          <tspan x="92" y="54.258">
                            by{' '}
                            {truncate(
                              item?.owner?.display_name || item?.owner,
                              32
                            )}
                          </tspan>
                        </text>
                        <text
                          fill="#fff"
                          style={{ whiteSpace: 'pre' }}
                          fontSize="16"
                          fontWeight="bold"
                          letterSpacing="0em"
                        >
                          <tspan x="92" y="35.24">
                            {truncate(item?.name, 28)}
                          </tspan>
                        </text>
                        <path
                          fill={`url(#${item?.image || 'no-playlist-image'})`}
                          d="M16 8H80V72H16z"
                        ></path>

                        {item?.image && (
                          <defs>
                            <pattern
                              id={item?.image}
                              width="1"
                              height="1"
                              patternContentUnits="objectBoundingBox"
                            >
                              <use
                                transform="scale(.00333)"
                                xlinkHref={`#${item?.image}-image`}
                              ></use>
                            </pattern>
                            <image
                              id={`${item?.image}-image`}
                              width="300"
                              height="300"
                              xlinkHref={item?.image}
                              preserveAspectRatio="xMinYMin slice"
                            ></image>
                          </defs>
                        )}
                      </>
                    ) : (
                      <text
                        fill="#fff"
                        style={{ whiteSpace: 'pre' }}
                        fontSize="24"
                        fontWeight="bold"
                        letterSpacing="-.04em"
                      >
                        <tspan x="16" y="41.86">
                          {truncate(item?.name, 24)}
                        </tspan>
                      </text>
                    )}
                  </g>
                </Flipped>
              );
            })}
          </g>
        );

        y += TITLE_HEIGHT + items.length * ROW_HEIGHT;
        return section;
      })}

      <defs>
        <pattern
          id="no-playlist-image"
          x="0"
          y="0"
          width="64"
          height="64"
          patternUnits="userSpaceOnUse"
        >
          <rect x="0" y="0" width="64" height="64" fill="#333" />
          <g transform="translate(32, 24)">
            <path
              d="M28 4.23199L11 7.84599V22.707C10.175 21.675 8.921 21 7.5 21C5.019 21 3 23.019 3 25.5C3 27.981 5.019 30 7.5 30C9.981 30 12 27.981 12 25.5V8.65499L27.001 5.46699V19.706C26.176 18.674 24.922 18 23.501 18C21.02 18 19.001 20.019 19.001 22.5C19.001 24.981 21.02 27 23.501 27C25.982 27 28.001 24.982 28.001 22.5L28 4.23199ZM7.5 29C5.57 29 4 27.43 4 25.5C4 23.57 5.57 22 7.5 22C9.43 22 11 23.57 11 25.5C11 27.43 9.43 29 7.5 29ZM23.501 26C21.571 26 20.001 24.43 20.001 22.5C20.001 20.57 21.571 19 23.501 19C25.431 19 27.001 20.57 27.001 22.5C27.001 24.43 25.431 26 23.501 26Z"
              fill="white"
            />
          </g>
        </pattern>
      </defs>
    </svg>
  );
}

const SectionedTrackList = connect(
  ({
    contextUri,
    playerState: { paused } = {},
    preview: { uri: currentPreviewUri, isPlaying: isPreviewPlaying } = {},
  }) => ({
    currentPreviewUri,
    isPreviewPlaying,
  }),
  actions
)(function SectionedTrackList(props) {
  const {
    type,
    sections,
    selectedItems,
    selected,
    onAdd,
    isSearching,
    currentPreviewUri,
    isPreviewPlaying,
  } = props;
  const TITLE_HEIGHT = 29;
  const ROW_HEIGHT = 80;

  const height =
    sections?.map(({ items }) => items).flat().length * ROW_HEIGHT + 92;
  let y = 0;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox={`0 0 375 ${height}`}
    >
      {sections?.map(({ name, items }) => {
        const section = (
          <g transform={`translate(0, ${isSearching ? 0 : TITLE_HEIGHT + y})`}>
            <Flipped flipId={`${type}-${name}-title`}>
              <g opacity={isSearching ? 0 : 1}>
                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="16"
                  fontWeight="450"
                  letterSpacing="0em"
                  opacity="0.7"
                >
                  <tspan x="16" y="0">
                    {name}
                  </tspan>
                </text>
              </g>
            </Flipped>

            <foreignObject
              x="0"
              y="0"
              width="375"
              height={items.length * ROW_HEIGHT}
            >
              {items.map((item) => {
                const {
                  uri,
                  name,
                  image: imageUrl,
                  primary_artist_name,
                  artists = [
                    {
                      name: primary_artist_name,
                    },
                  ],
                } = item;

                return (
                  <Flipped
                    key={uri}
                    flipId={uri}
                    onAppear={onAppear}
                    onExit={onExit}
                  >
                    <div className="Playlist__track-row-wrapper">
                      <TrackRow
                        uri={uri}
                        title={name}
                        subtitle={artists.map(({ name }) => name).join(', ')}
                        imageUrl={imageUrl}
                        onClick={(event) => {
                          event.stopPropagation();

                          if (uri === currentPreviewUri && isPreviewPlaying)
                            props.pausePreview(uri);
                          else props.playPreview(uri);
                        }}
                        canPreview
                        actionButton={
                          selected && selected.has(uri) ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8.33 15.62L4.32 10.93L5.3 9.77L8.33 13.31L14.66 5.9L15.8 6.87L8.33 15.62Z"
                                fill="white"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10.0008 4.16667H9.1675V9.16667H4.1675V10H9.1675V15H10.0008V10H15.0008V9.16667H10.0008V4.16667ZM9.58333 0C4.29083 0 0 4.29083 0 9.58333C0 14.8758 4.29083 19.1667 9.58333 19.1667C14.8758 19.1667 19.1667 14.8758 19.1667 9.58333C19.1667 4.29083 14.8758 0 9.58333 0ZM9.58333 18.3333C4.75833 18.3333 0.833333 14.4083 0.833333 9.58333C0.833333 4.75833 4.75833 0.833333 9.58333 0.833333C14.4083 0.833333 18.3333 4.75833 18.3333 9.58333C18.3333 14.4083 14.4083 18.3333 9.58333 18.3333Z"
                                fill="#B3B3B3"
                              />
                            </svg>
                          )
                        }
                        onActionButtonClick={(event) => {
                          event.stopPropagation();

                          onAdd(uri);
                        }}
                      />
                    </div>
                  </Flipped>
                );
              })}
            </foreignObject>
          </g>
        );

        y += TITLE_HEIGHT + items.length * ROW_HEIGHT;

        return section;
      })}

      <defs>
        <pattern
          id="no-playlist-image"
          x="0"
          y="0"
          width="64"
          height="64"
          patternUnits="userSpaceOnUse"
        >
          <rect x="0" y="0" width="64" height="64" fill="#333" />
          <g transform="translate(32, 24)">
            <path
              d="M28 4.23199L11 7.84599V22.707C10.175 21.675 8.921 21 7.5 21C5.019 21 3 23.019 3 25.5C3 27.981 5.019 30 7.5 30C9.981 30 12 27.981 12 25.5V8.65499L27.001 5.46699V19.706C26.176 18.674 24.922 18 23.501 18C21.02 18 19.001 20.019 19.001 22.5C19.001 24.981 21.02 27 23.501 27C25.982 27 28.001 24.982 28.001 22.5L28 4.23199ZM7.5 29C5.57 29 4 27.43 4 25.5C4 23.57 5.57 22 7.5 22C9.43 22 11 23.57 11 25.5C11 27.43 9.43 29 7.5 29ZM23.501 26C21.571 26 20.001 24.43 20.001 22.5C20.001 20.57 21.571 19 23.501 19C25.431 19 27.001 20.57 27.001 22.5C27.001 24.43 25.431 26 23.501 26Z"
              fill="white"
            />
          </g>
        </pattern>
      </defs>
    </svg>
  );
});

const truncate = (str, maxLen) =>
  str?.length > maxLen ? `${str.substr(0, maxLen - 1)}…` : str;
